// @ts-nocheck

import React, { Component, Fragment } from 'react';
import { injectIntl, FormattedMessage, IntlShape } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import PayByBankForm from './PayByBankForm/PayByBankForm';
import { PaymentFooter } from './PaymentFooter';

import { isFrictionlessCheckoutEligibleBL } from 'businessLogic/Auth/Auth';
import { displayGratuityFeature } from 'businessLogic/Payment/Gratuity';
import { isMAIPasPDF } from 'businessLogic/Payment/helpers';
import { isWalletTypeOfBank, isWalletTypeOfCreditCard } from 'businessLogic/Wallet/helpers';
import BatchInvoiceListTable from 'components/BatchPayments/BatchInvoiceListTable';
import { getAllowedBatchPaymentMethods } from 'components/BatchPayments/helpers';
import ScheduleAndAutoPay from 'components/Core/Autopay-schedule/ScheduleAndAutoPay/ScheduleAndAutoPay';
import BankTransfer from 'components/Core/Payment/Bank/Transfer/BankTransfer';
import BankUpdate from 'components/Core/Payment/Bank/Update/BankUpdate';
import CardPayment from 'components/Core/Payment/Card/Payment/CardPayment';
import CardUpdate from 'components/Core/Payment/Card/Update/CardUpdate';
import Gratuity from 'components/Core/Payment/Gratuity/Gratuity';
import PayPalCheckoutTabs from 'components/Core/Payment/Growth/PayPalCheckoutExperiment/PayPalCheckout';
import { checkPlaidExperimentEnabled } from 'components/Core/Payment/Growth/PlaidExperiment/utils/checkPlaidExperimentEnabled';
import BankForm from 'components/Core/Payment/PaymentForm/Bank/BankForm';
import CardForm from 'components/Core/Payment/PaymentForm/Card/CardForm';
import PayPalCardForm from 'components/Core/Payment/PaymentForm/PayPalCardForm/PayPalCardForm';
import SSRSuspense from 'components/Core/Payment/PaymentForm/SSRSuspense/SSRSuspense';
import DesktopPaymentMethodTabs from 'components/Core/Payment/PaymentMethod/DesktopPaymentMethodTabs/DesktopPaymentMethodTabs';
import MobilePaymentMethodWrapper from 'components/Core/Payment/PaymentMethod/MobilePaymentMethodTabs/MobilePaymentMethodTabs';
import SavedPaymentMethods from 'components/Core/Payment/PaymentMethod/SavePaymentMethod/SavedPaymentMethods';
import DeleteWalletModal from 'components/Core/Payment/Wallet/DeleteWalletModal/DeleteWalletModal';
import TrustSection from 'components/Core/TrustSection/TrustSection';
import ContactDetails from 'components/PaymentReq/OneToMany/ContactDetails/ContactDetails';
import FormSectionHeader from 'components/PaymentReq/OneToMany/FormSectionHeader/FormSectionHeader';
import Button from 'components/Shared/Button/Button';
import SaveToMerchantWalletCheckbox from 'components/Shared/CardOnFile/components/SaveToMerchantWalletCheckbox';
import { FrictionlessDetails } from 'components/Shared/FrictionlessCheckout/FrictionlessDetails';
import FrictionlessLogin from 'components/Shared/FrictionlessLogin';
import MessageCard from 'components/Shared/MessageCard/MessageCard';
import Separator from 'components/Shared/Separator/Separator';
import SpinnerCentered from 'components/Shared/Spinner/SpinnerCentered/SpinnerCentered';
import {
  filterSurchargeEligibleMethods,
  getPayWithMessages,
} from 'components/Shared/utils/surchargeHelpers';
import FormDraftProvider from 'contexts/FormDraftProvider';
import SegmentIO from 'reporting/SegmentIO';
import { getIESMaxAllowedPayAmountConfig, isCOFEligiblePaymentMethod } from 'shared/PaymentHelpers';
import { getEnabledPaymentMethod, getPaymentMethodsOrder } from 'shared/clientUtils';
import {
  isIdentitySignInWidgetCompletedSelector,
  isSignInCTADecisionPendingSelector,
  shouldShowSignInCTASelector,
} from 'store/auth/selectors';
import { authActions } from 'store/auth/slice';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { insightSelectors } from 'store/insight/selectors';
import {
  isFrictionlessLoginPhase3Enabled,
  isBatchPaymentsEnabled,
  isBatchCOFEnabled,
  isInNameValidationExperiment,
  isInEnhancementsHoldoutExperiment,
} from 'store/ixp/selector';
import { modalActions } from 'store/modal/slice';
import {
  walletTokenizationPaymentCreation,
  paymentMethodTypeChange,
  paymentActions,
} from 'store/payment/slice';
import {
  saleSelectors,
  isForcedAutopayRecurringSelector,
  isSurchargingEnabledSelector,
} from 'store/sale/selectors';
import { toastActions } from 'store/toast/slice';
import { walletSelectors } from 'store/wallet/selectors';
import { walletActions, changeSelectedWallet, deleteWallet } from 'store/wallet/slice';
import { breakpoints, colors, fontSize } from 'styles/cp';
import { Auth } from 'types/Auth';
import { Config } from 'types/Config';
import { FeatureFlags } from 'types/FeatureFlags';
import { IXP } from 'types/IXP';
import { Insight } from 'types/Insight';
import { Payment as PaymentTypes } from 'types/Payment';
import { Sale } from 'types/Sale';
import { Wallet } from 'types/Wallet';
import { PaymentMethod, TXN_MAP, WALLET_MAP, SURCHARGE_MAP } from 'types/constants';

const LazyNanopay = React.lazy(() => import('components/Core/Nanopay/Nanopay'));
const LazyNanopayEmpty = React.lazy(() => import('components/Core/Nanopay/Empty/NanopayEmpty'));

// rest of the cards have same name in wallet widget
const WALLET_WIDGET_NAME_ADAPTER = {
  amex: 'american-express',
  diners: 'diners-club',
  masterCard: 'mastercard',
};

type PaymentProps = {
  companyLocale?: string;
  auth: Auth;
  featureFlags: FeatureFlags;
  config: Config;
  payment: Pick<
    PaymentTypes,
    'paymentMethodType' | 'isPayPalCommerceInvoice' | 'paymentStatus' | 'balanceAmount' | 'country',
    'paymentMethodSubType'
  >;
  wallet: Pick<
    Wallet,
    | 'currentlyUpdatingWalletId'
    | 'pendingDeleteWallet'
    | 'updateWalletStatus'
    | 'deleteWalletStatus'
    | 'userWallets'
    | 'enabledPaymentMethods'
    | 'enabledCCTypes'
    | 'selectedWalletId'
    | 'bankCreatePaymentTrigger'
    | 'cardCreatePaymentTrigger'
    | 'isPPAAMInstallmentsEnabled'
    | 'fetchWalletStatus'
  >;
  paymentMethodsOrder: any[];
  isSubscription: boolean;
  setIsNewWalletUI: Function;
  showToast: Function;
  intl: IntlShape;
  onUpdatePaymentMethodClicked: (...args: any[]) => any;
  showModal: Function;
  onDeletePaymentMethodClicked: Function;
  deleteWallet: Function;
  cancelDeleteWallet: Function;
  changeSelectedWallet: Function;
  paymentMethodTypeChange: (paymentMethod: PaymentMethod | string) => any;
  isInvoiceGratuityEnabled: boolean;
  companyName: string;
  ixp: IXP;
  createPayment: (...args: any[]) => any;
  bindFormSubmission: (...args: any[]) => any;
  sale: Pick<Sale, 'amount'>;
  updateCountry: (...args: any[]) => any;
  stopPayment: (...args: any[]) => any;
  // NOTE: The below function is a candidate to be removed. Possible dead code.
  stopPaymentProgress?: (...args: any[]) => any;
  onCvvInputValueChange: (...args: any[]) => any;
  hideModal: Function;
  insight: Insight;
  isPPCheckoutExperiment: boolean;
  isPPExperimentTreatmentA: boolean;
  isPPExperimentTreatmentB: boolean;
  isOneToMany: boolean;
  isFrictionlessPhase3Enabled: boolean;
  shouldShowSignInCTA?: boolean;
  isSignInCTADecisionPending?: boolean;
  isForcedAutoPay: boolean;
  setSaveToMerchantWalletCheckboxChecked: (checked: boolean) => void;
  isInNameValidationExperiment: boolean;
  isInEnhancementsHoldoutExperiment: boolean;
  isSurchargingEnabled?: boolean;
};

type PaymentState = {
  showPaymentTabs: boolean;
  shouldUseNewWalletFlow: boolean;
};

class Payment extends Component<PaymentProps, PaymentState> {
  constructor(props: PaymentProps) {
    super(props);
    this.state = {
      showPaymentTabs: false,
    };
  }

  componentDidMount() {
    let showPaymentTabs;
    const { auth, isSubscription, setIsNewWalletUI } = this.props;
    showPaymentTabs = !auth.isUserSignedIn;

    if (isSubscription) {
      // updating a flag in the wallet store to be used in logger
      setIsNewWalletUI(true);
    }

    this.setState({ showPaymentTabs, shouldUseNewWalletFlow: isSubscription });
  }

  componentDidUpdate(prevProps: PaymentProps) {
    // *this should be revisit*
    // check if this update is after a sucsseful update
    if (
      !this.props.wallet.currentlyUpdatingWalletId &&
      this.props.wallet.updateWalletStatus === TXN_MAP.STATUS.SUCCESS &&
      prevProps.wallet.updateWalletStatus !== TXN_MAP.STATUS.SUCCESS
    ) {
      const { updatedWallet = {} } = this.props.wallet;
      if (isWalletTypeOfBank(updatedWallet.walletType)) {
        this.props.showToast({
          text: this.props.intl.formatMessage(
            {
              id: 'PAYFLOW_ACOUNT_UPDATED',
            },
            {
              0: `${updatedWallet.accountType}${updatedWallet.accountNumber}`,
            }
          ),
        });
      } else if (isWalletTypeOfCreditCard(updatedWallet.walletType)) {
        const { cardType, cardNumber } = updatedWallet;
        const cardRepresentation = `${cardType?.toUpperCase()}...${cardNumber.slice(-4)}`;
        this.props.showToast({
          text: this.props.intl.formatMessage(
            {
              id: 'PAYFLOW_ACOUNT_UPDATED',
            },
            {
              0: cardRepresentation,
            }
          ),
        });
      }
    }
    if (
      this.props.wallet.deleteWalletStatus === TXN_MAP.STATUS.SUCCESS &&
      prevProps.wallet.deleteWalletStatus !== TXN_MAP.STATUS.SUCCESS
    ) {
      const { pendingDeleteWallet = {} } = this.props.wallet;
      if (isWalletTypeOfBank(pendingDeleteWallet.walletType)) {
        this.props.showToast({
          text: this.props.intl.formatMessage(
            {
              id: 'PAYFLOW_ACOUNT_DELETED',
            },
            {
              0: `${pendingDeleteWallet.accountType}`,
              1: `${pendingDeleteWallet.accountNumber}`,
            }
          ),
        });
      } else if (isWalletTypeOfCreditCard(pendingDeleteWallet.walletType)) {
        const creditOrDebit = 'credit'; // Can we even know that?
        const { cardType, cardNumber } = pendingDeleteWallet;
        const cardRepresentation = `${cardType?.toUpperCase()}...${cardNumber.slice(-4)}`;
        this.props.showToast({
          text: this.props.intl.formatMessage(
            {
              id: 'PAYFLOW_CARD_DELETED',
            },
            {
              0: `${creditOrDebit}`,
              1: `${cardRepresentation}`,
            }
          ),
        });
      }
    }
  }

  onUpdatePaymentMethodClicked = (id, walletType) => {
    this.props.onUpdatePaymentMethodClicked({ id });

    SegmentIO.transactionEngaged({
      activity_type: 'wallet',
      ui_action: 'clicked',
      ui_object: 'button',
      ui_object_detail: 'edit_wallet',
      ui_access_point: 'menu_all',
    });

    const componentToShow = isWalletTypeOfBank(walletType) ? BankUpdate : CardUpdate;
    this.props.showModal({
      component: componentToShow,
      componentProps: {
        closeOnEsc: true,
      },
    });
  };
  onDeletePaymentMethodClicked = (id, bankOrCard) => {
    this.props.onDeletePaymentMethodClicked({ id });

    SegmentIO.transactionEngaged({
      activity_type: 'wallet',
      ui_action: 'clicked',
      ui_object: 'button',
      ui_object_detail: 'delete_wallet',
      ui_access_point: 'menu_all',
    });

    this.props.showModal({
      component: DeleteWalletModal,
      componentProps: {
        closeOnEsc: true,
        onOk: async () => {
          await this.props.deleteWallet({
            id,
            bankOrCard,
          });
          const typeToFilterBy = bankOrCard === 'bank' ? 'bank' : 'card';
          const wallets = this.props.wallet.userWallets?.filter(
            (wallet) => wallet.walletType === typeToFilterBy
          );
          const addWalletId = bankOrCard === 'bank' ? 'AddBank' : 'AddCard';
          const newWalletId = wallets[0] ? wallets[0].id : addWalletId;
          this.changePaymentMethod({
            id: newWalletId,
          });
        },
        onCancel: () => {
          this.props.cancelDeleteWallet();
        },
      },
    });
  };
  switchToPayForm = () => {
    const { paymentMethodType } = this.props.payment;
    this.props.changeSelectedWallet(paymentMethodType === 'bank' ? 'AddBank' : 'AddCard');
    this.setState({ showPaymentTabs: true });
  };
  switchToSavedPaymentMethods = () => {
    const { userWallets } = this.props.wallet;
    const defaultWallet = userWallets?.find((wallet) => wallet.default);
    const walletId = defaultWallet ? defaultWallet.id : userWallets[0].id;
    this.props.changeSelectedWallet(walletId);
    this.setState({ showPaymentTabs: false });
  };
  // Can be removed after we officially use the new flow
  changePaymentMethod = ({ id }) => {
    this.props.changeSelectedWallet(id);
    this.props.paymentMethodTypeChange({ paymentMethodType: this.props.payment.paymentMethodType });
  };

  onCheckboxChange = () => {
    const { setSaveToMerchantWalletCheckboxChecked } = this.props;
    const isCurrentlyChecked = walletSelectors.isSaveToMerchantWalletCheckboxChecked(
      this.props.wallet
    );
    setSaveToMerchantWalletCheckboxChecked(!isCurrentlyChecked);
  };

  render() {
    const {
      config,
      wallet: {
        enabledPaymentMethods,
        enabledCCTypes,
        selectedWalletId,
        bankCreatePaymentTrigger,
        cardCreatePaymentTrigger,
        userWallets,
        fetchWalletStatus,
      },
      payment: { isPayPalCommerceInvoice, paymentMethodType, paymentStatus, bankList = [] },
      paymentMethodsOrder,
      auth: { isUserSignedIn, countryCode },
      ixp,
      isInvoiceGratuityEnabled = false,
      featureFlags,
      createPayment,
      companyLocale,
      region,
      bindFormSubmission,
      paymentMethodTypeChange,
      isSubscription,
      sale,
      isPPCheckoutExperiment,
      isPPExperimentTreatmentA,
      isPPExperimentTreatmentB,
      isOneToMany,
      isFrictionlessPhase3Enabled,
      shouldShowSignInCTA,
      isSignInCTADecisionPending,
      isIdentitySignInWidgetCompleted,
      isForcedAutoPay,
      isSurchargingEnabled,
    } = this.props;
    const { shouldUseNewWalletFlow } = this.state;
    const isPaypalORVenmo = ['paypal_ppaam', 'venmo'].includes(paymentMethodType);
    const isAffirmBnpl = paymentMethodType === 'affirm_bnpl';
    const isOnlyPaypalORVenmo =
      (enabledPaymentMethods.includes('paypal_ppaam') || enabledPaymentMethods.includes('venmo')) &&
      !enabledPaymentMethods.some((item) => item !== 'paypal_ppaam' && item !== 'venmo');
    let currentMaxAllowedPaymentAmountConfig;

    const isIESCompany = featureFlags['cp-increase-max-amount-limit'];
    if (isIESCompany) {
      const maxPaymentAmountConfig = getIESMaxAllowedPayAmountConfig({
        payment: this.props.payment,
        sale,
      });
      const paymentMethodConfig = maxPaymentAmountConfig.forPaymentMethod[paymentMethodType];
      const inputAmount = this.props.payment.inputAmount;
      const isAmountExceedingMax =
        paymentMethodConfig?.maxAllowedValue && inputAmount > paymentMethodConfig.maxAllowedValue;

      if (isAmountExceedingMax) {
        currentMaxAllowedPaymentAmountConfig = {
          ...paymentMethodConfig,
          generalMaxAllowedAmount: maxPaymentAmountConfig.general.maxAllowedAmount,
        };
      }
    }

    const {
      endpoints: { cdn },
    } = config;
    // If there is only one payment option available, we need to show the user a message.
    const maybeShowUserPaymentOptionMessage = () => {
      // For batch payments, use the dynamically calculated paymentMethodsOrder
      // For regular payments, use wallet.enabledPaymentMethods
      const isBatchPayments = this.props.isBatchPaymentsEnabled;
      const methodsToCheck = isBatchPayments ? paymentMethodsOrder : enabledPaymentMethods;

      // Out of the enabledPaymentMethods, filter only the payment options that are on the PaymentMethodTabs => bank, cc, dc, ap or paypal.
      // enabledPaymenntOptions: ['bank', 'cc', 'dc', 'ap', 'pp'];
      let enabledPaymentOptions = getEnabledPaymentMethod(methodsToCheck);
      if (!Array.isArray(enabledPaymentOptions)) {
        return null;
      }
      // If there is more than one payment options, then no need to show message.
      if (enabledPaymentOptions.length > 1) {
        return null;
      }
      // There is only one payment option. Currently, only show this message for bank or credit card, so if it isn't one of those, no need to show the message.
      const { bankMessageId, bankDefaultMessage, ccMessageId, ccDefaultMessage } =
        getPayWithMessages(isSurchargingEnabled);
      if (enabledPaymentOptions[0] === 'bank') {
        return (
          <label className="pay-with-title">
            <FormattedMessage
              id={bankMessageId}
              defaultMessage={bankDefaultMessage}
              values={
                isSurchargingEnabled ? { feePercent: SURCHARGE_MAP.ACH_FEE_LABEL } : undefined
              }
            />
          </label>
        );
      } else if (enabledPaymentOptions[0] === 'cc') {
        return (
          <label className="pay-with-title">
            <FormattedMessage
              id={ccMessageId}
              defaultMessage={ccDefaultMessage}
              values={isSurchargingEnabled ? { feePercent: SURCHARGE_MAP.CC_FEE_LABEL } : undefined}
            />
          </label>
        );
      } else if (enabledPaymentOptions[0] === 'nanopay') {
        return (
          <label className="pay-with-title">
            <FormattedMessage
              id="PAYNOW_PAYNOW_PAY_WITH_BANK_PAYMENT"
              defaultMessage="Pay with bank payment"
            />
          </label>
        );
      }
      return null;
    };
    const getAllowedCardTypes = () => {
      return enabledCCTypes.map(
        (allowedCardType) => WALLET_WIDGET_NAME_ADAPTER[allowedCardType] || allowedCardType
      );
    };
    const getSelectedPaymentMethodContent = () => {
      if (
        isPayPalCommerceInvoice &&
        (paymentMethodType === 'dc' || paymentMethodType === 'cc' || paymentMethodType === 'dc,cc')
      ) {
        return (
          <PayPalCardForm
            config={this.props.config}
            wallet={this.props.wallet}
            auth={this.props.auth}
            payment={this.props.payment}
            paymentAmountChange={this.props.paymentAmountChange}
            key={this.props.payment}
            sale={this.props.sale}
          />
        );
      }
      if (paymentMethodType === 'nanopay') {
        const {
          config: { nanopay: { minimumAmountForBankPayment = 1 } = {} } = {},
          insight: { isPartiallyPaid } = {},
        } = this.props;
        if (this.props.sale.amount >= minimumAmountForBankPayment && !isPartiallyPaid) {
          return (
            <SSRSuspense fallback={<SpinnerCentered />}>
              <LazyNanopay config={this.props.config} />
            </SSRSuspense>
          );
        } else {
          return (
            <SSRSuspense fallback={<SpinnerCentered />}>
              <LazyNanopayEmpty companyName={this.props.companyName} cdn={cdn} />
            </SSRSuspense>
          );
        }
      }
      if (isPaypalORVenmo) {
        return <Fragment />;
      }
      if (['bank', 'dc', 'cc', 'dc,cc', 'eft', 'pbb'].includes(paymentMethodType)) {
        return getBankOrCCPaymentMethodContent();
      }
      return null;
    };

    const getBankOrCCPaymentMethodContent = () => {
      if (shouldUseNewWalletFlow) {
        const formProps = {
          paymentStatus,
          createPayment,
          stopPayment: paymentActions.cancelPayment,
          bindFormSubmission,
        };
        if (paymentMethodType === 'bank') {
          const _bankFormProps = {
            ...formProps,
            boundCreateBankFn: bankCreatePaymentTrigger,
          };

          return (
            <FormDraftProvider.Consumer>
              {({ updateDraft, bank }) => (
                <BankForm
                  bankDraft={bank}
                  paymentMethodType={paymentMethodType}
                  updateFormDraft={(draft) => updateDraft(paymentMethodType, draft)}
                  isInNameValidationExperiment={this.props.isInNameValidationExperiment}
                  isInEnhancementsHoldoutExperiment={this.props.isInEnhancementsHoldoutExperiment}
                  {..._bankFormProps}
                />
              )}
            </FormDraftProvider.Consumer>
          );
        }
        if (['dc', 'cc', 'dc,cc'].includes(paymentMethodType)) {
          const _cardFormProps = {
            ...formProps,
            paymentMethodType,
            boundCreateCardFn: cardCreatePaymentTrigger,
            bindFormSubmission,
            endpoints: config.endpoints,
            companyLocale,
            enabledPaymentMethods,
            featureFlags,
            countryCode,
            country: this.props.payment.country,
            updateCountry: this.props.updateCountry,
            isCreditFloorDecline: !!this.props.payment.creditFloorDeclineAmount,
          };
          return (
            <FormDraftProvider.Consumer>
              {(ctx) => (
                <CardForm
                  cardDraft={ctx[paymentMethodType]}
                  updateFormDraft={(draft) => ctx.updateDraft(paymentMethodType, draft)}
                  allowedCardTypes={getAllowedCardTypes()}
                  {..._cardFormProps}
                />
              )}
            </FormDraftProvider.Consumer>
          );
        }
      } else {
        // Handle bank/EFT payment methods
        if (paymentMethodType === 'bank' || paymentMethodType === 'eft') {
          return (
            <BankTransfer
              bindFormSubmission={this.props.bindFormSubmission}
              isUserSignedIn={isUserSignedIn}
              config={config}
              createPayment={this.props.createPayment}
              stopPayment={this.props.stopPayment}
              changeSelectedBank={this.changePaymentMethod}
              stopPaymentProgress={this.props.stopPaymentProgress}
              onDeletePaymentMethodClicked={this.onDeletePaymentMethodClicked}
              onUpdatePaymentMethodClicked={this.onUpdatePaymentMethodClicked}
              wallet={this.props.wallet}
              payment={this.props.payment}
              showModal={this.props.showModal}
              hideModal={this.props.hideModal}
              isInNameValidationExperiment={this.props.isInNameValidationExperiment}
              isInEnhancementsHoldoutExperiment={this.props.isInEnhancementsHoldoutExperiment}
              ixp={ixp}
              invoiceId={saleSelectors.localIdSelector(this.props.sale)}
              domainId={this.props.domainId}
              recipientEmail={this.props.auth?.recipientEmail}
            />
          );
        }

        if (['dc', 'cc', 'dc,cc'].includes(paymentMethodType)) {
          return (
            <CardPayment
              bindFormSubmission={this.props.bindFormSubmission}
              auth={this.props.auth}
              config={this.props.config}
              createPayment={this.props.createPayment}
              stopPayment={this.props.stopPayment}
              changeSelectedCard={this.changePaymentMethod}
              stopPaymentProgress={this.props.stopPaymentProgress}
              onDeletePaymentMethodClicked={this.onDeletePaymentMethodClicked}
              onUpdatePaymentMethodClicked={this.onUpdatePaymentMethodClicked}
              wallet={this.props.wallet}
              featureFlags={this.props.featureFlags}
              payment={this.props.payment}
              companyLocale={this.props.companyLocale}
              onCvvInputValueChange={this.props.onCvvInputValueChange}
              ixp={ixp}
              updateCountry={this.props.updateCountry}
            />
          );
        }
        if (paymentMethodType === 'pbb') {
          return <PayByBankForm bankList={bankList} paymentStatus={paymentStatus} />;
        }
      }
    };
    // Don't show "add payment method" UI while wallets are still being fetched for signed-in users
    // This prevents a race condition where the checkbox appears briefly before wallet data loads
    const isWalletFetchComplete =
      fetchWalletStatus === WALLET_MAP.FETCH_STATUS.FETCH_SUCCESS ||
      fetchWalletStatus === WALLET_MAP.FETCH_STATUS.FETCH_FAILED ||
      !isUserSignedIn;
    const isAddingBank =
      (paymentMethodType === 'bank' || paymentMethodType === 'eft') &&
      (selectedWalletId === 'AddBank' || (!selectedWalletId && isWalletFetchComplete));
    const isAddingCard =
      ['cc', 'dc', 'dc,cc'].includes(paymentMethodType) &&
      (selectedWalletId === 'AddCard' || (!selectedWalletId && isWalletFetchComplete));
    const isAddingPaymentMethod = isAddingBank || isAddingCard;
    const shouldShowSavePaymentMethodCta =
      isAddingPaymentMethod &&
      !isSubscription &&
      !isPaypalORVenmo &&
      !isPayPalCommerceInvoice &&
      !isAffirmBnpl &&
      !isForcedAutoPay;
    const doesUserHaveWallets = userWallets && userWallets.length > 0;
    const shouldShowPaymentTabs =
      this.state.showPaymentTabs ||
      !doesUserHaveWallets ||
      isPayPalCommerceInvoice ||
      !shouldUseNewWalletFlow;
    const shouldShowBackToSavedMethodsBtn =
      isUserSignedIn && doesUserHaveWallets && !isPayPalCommerceInvoice && shouldUseNewWalletFlow;
    const isMaipASPDFEnabled = isMAIPasPDF({ sale, featureFlags });
    const isFrictionlessCheckoutEnabled =
      isFrictionlessCheckoutEligibleBL({
        region,
        featureFlags,
        insight: this.props.insight,
        auth: this.props.auth,
        sale,
      }) &&
      !isIdentitySignInWidgetCompleted &&
      !isForcedAutoPay;
    const isInFrictionlessPhase3Experience =
      isFrictionlessPhase3Enabled && (shouldShowSignInCTA || isSignInCTADecisionPending);

    // if in FL Phase 3 experience - show FL phase 2 footer only when finished the email vewrification call to Identity
    const shouldShowFrictionlessCheckoutFooter =
      isFrictionlessCheckoutEnabled && !isInFrictionlessPhase3Experience;
    const allowedPaymentMethods = paymentMethodsOrder;
    const showPaymentMethodsHeader: boolean =
      paymentMethodsOrder?.length > 1 && !this.props.isBatchPaymentsEnabled;
    const isCheckboxChecked = walletSelectors.isSaveToMerchantWalletCheckboxChecked(
      this.props.wallet
    );

    // Show SaveToMerchantWalletCheckbox when:
    // 1. Payment method is COF eligible (cc, dc, bank)
    // 2. cardOnFile exists (server sets this only for eligible customers)
    // 3. Either single invoice flow OR batch flow with isBatchCOFEnabled
    const isSingleInvoiceWithCOF =
      !this.props.isBatchPaymentsEnabled && this.props.wallet?.cardOnFile;
    const isBatchWithCOF = this.props.isBatchCOFEnabled && this.props.wallet?.cardOnFile;
    const { isInPlaidExperimentAnyGroup } = ixp
      ? checkPlaidExperimentEnabled(ixp)
      : { isInPlaidExperimentAnyGroup: false };
    const shouldShowSaveToMerchantWalletCheckbox =
      isCOFEligiblePaymentMethod(paymentMethodType) && (isSingleInvoiceWithCOF || isBatchWithCOF);
    return (
      <Fragment>
        <FormDraftProvider.Provider>
          {isFrictionlessCheckoutEnabled && !isInPlaidExperimentAnyGroup && (
            <FrictionlessDetails
              isEmailUpdateRequired={isMaipASPDFEnabled || isOneToMany}
              showPaymentMethodsHeader={showPaymentMethodsHeader}
              shouldShowContactName={isOneToMany}
              isFrictionlessPhase3Enabled={isFrictionlessPhase3Enabled}
              cdn={config.endpoints.cdn}
              updateFrictionlessLoginCountry={this.props.updateFrictionlessLoginCountry}
            />
          )}
          {(isMaipASPDFEnabled || isOneToMany) && !isFrictionlessCheckoutEnabled && (
            <Fragment>
              <ContactDetails shouldShowContactName={isOneToMany} />
              <Separator height={12} />
              <FormSectionHeader content="PAYMENT DETAILS" />
              <Separator height={12} />
            </Fragment>
          )}
          {isFrictionlessCheckoutEnabled && this.props.isBatchPaymentsEnabled && (
            <Separator height={12} />
          )}
          {this.props.isBatchPaymentsEnabled && <BatchInvoiceListTable />}
          {shouldShowPaymentTabs ? (
            <Fragment>
              <div className="new-payment-method-header">
                {shouldUseNewWalletFlow && (
                  <p>
                    <FormattedMessage
                      id="PAYFORM_SELECT_PAYMENT_METHOD"
                      defaultMessage="Select payment method"
                    />
                  </p>
                )}
                {shouldShowBackToSavedMethodsBtn && (
                  <div className="back-btn">
                    <Button
                      buttonType="tertiary"
                      size="standard"
                      padding="0"
                      onClick={this.switchToSavedPaymentMethods}
                    >
                      Go back to saved payment methods
                    </Button>
                  </div>
                )}
              </div>
              {isPPExperimentTreatmentA && (
                <>
                  <PayPalCheckoutTabs
                    payment={this.props.payment}
                    allowedPaymentMethods={allowedPaymentMethods}
                    isPPCheckoutExperiment={isPPExperimentTreatmentA}
                  />
                  {!isOnlyPaypalORVenmo && (
                    <span className="payment-method-label">
                      <FormattedMessage
                        id="SELECT_PAYMENT_METHOD"
                        defaultMessage="OR SELECT PAYMENT METHOD"
                      />
                    </span>
                  )}
                </>
              )}
              <div className="payment-tabs-desktop">
                <DesktopPaymentMethodTabs
                  ixp={ixp}
                  paymentMethodTypeChange={paymentMethodTypeChange}
                  changeSelectedWallet={this.props.changeSelectedWallet}
                  payment={this.props.payment}
                  allowedPaymentMethods={allowedPaymentMethods}
                  featureFlags={this.props.featureFlags}
                  shouldUseNewWalletFlow={shouldUseNewWalletFlow}
                  wallet={this.props.wallet}
                  isPPCheckoutExperiment={isPPCheckoutExperiment}
                  isSurchargingEnabled={isSurchargingEnabled}
                />
              </div>
              <div className="payment-tabs-mobile" data-testid="mobile-payment-selector">
                <MobilePaymentMethodWrapper
                  paymentMethodTypeChange={paymentMethodTypeChange}
                  changeSelectedWallet={this.props.changeSelectedWallet}
                  isUserSignedIn={isUserSignedIn}
                  showModal={this.props.showModal}
                  featureFlags={this.props.featureFlags}
                  hideModal={this.props.hideModal}
                  payment={this.props.payment}
                  insight={this.props.insight}
                  allowedPaymentMethods={allowedPaymentMethods}
                  shouldUseNewWalletFlow={shouldUseNewWalletFlow}
                  enabledPaymentMethods={enabledPaymentMethods}
                  enabledCCTypes={enabledCCTypes}
                  cdn={cdn}
                  ixp={ixp}
                  wallet={this.props.wallet}
                  isPPCheckoutExperiment={isPPCheckoutExperiment}
                  isOnlyPaypalORVenmo={isOnlyPaypalORVenmo}
                  isSurchargingEnabled={isSurchargingEnabled}
                />
              </div>

              {maybeShowUserPaymentOptionMessage()}
              {getSelectedPaymentMethodContent()}
              {currentMaxAllowedPaymentAmountConfig && (
                <MessageCard
                  text={'IES_WARN_MESSAGE_AMOUNT_EXCEED'}
                  messageValues={{
                    0: currentMaxAllowedPaymentAmountConfig.maxAllowedAmount,
                    1: currentMaxAllowedPaymentAmountConfig.name,
                    2: currentMaxAllowedPaymentAmountConfig.generalMaxAllowedAmount,
                  }}
                  status="orangeWarn"
                  messageFontSize="14px"
                  errorMessageIconPosition="start"
                />
              )}
              {!isInPlaidExperimentAnyGroup && (
                <PaymentFooter
                  shouldShowSavePaymentMethodCta={shouldShowSavePaymentMethodCta}
                  isAddingCard={isAddingCard}
                  enabledPaymentMethods={enabledPaymentMethods}
                  enabledCCTypes={enabledCCTypes}
                  cdn={cdn}
                  isFrictionlessCheckoutEnabled={shouldShowFrictionlessCheckoutFooter}
                  updateFrictionlessLoginCountry={this.props.updateFrictionlessLoginCountry}
                  shouldShowSaveToMerchantWalletCheckbox={shouldShowSaveToMerchantWalletCheckbox}
                />
              )}
            </Fragment>
          ) : (
            <SavedPaymentMethods
              onUpdatePaymentMethodClicked={this.onUpdatePaymentMethodClicked}
              onDeletePaymentMethodClicked={this.onDeletePaymentMethodClicked}
              onAddPaymentMethodClicked={this.switchToPayForm}
              bindFormSubmission={bindFormSubmission}
            />
          )}
          {!isMaipASPDFEnabled &&
            !isInPlaidExperimentAnyGroup &&
            (!this.props.isBatchPaymentsEnabled || this.props.onlyDefaultInvoiceSelected) && (
              <ScheduleAndAutoPay isForcedAutoPay={isForcedAutoPay} />
            )}
          {!isInPlaidExperimentAnyGroup && shouldShowSaveToMerchantWalletCheckbox && (
            <SaveToMerchantWalletCheckbox
              onCheckboxChange={this.onCheckboxChange}
              isCheckboxChecked={isCheckboxChecked}
              companyName={this.props.companyName}
            />
          )}
          {isInvoiceGratuityEnabled &&
            (!this.props.isBatchPaymentsEnabled || this.props.onlyDefaultInvoiceSelected) && (
              <Gratuity />
            )}
          {isPPExperimentTreatmentB && (
            <PayPalCheckoutTabs
              payment={this.props.payment}
              allowedPaymentMethods={allowedPaymentMethods}
              isPPCheckoutExperiment={isPPExperimentTreatmentB}
            />
          )}
          <div className="truste-w">
            <TrustSection cdn={config.endpoints.cdn} />
          </div>
          {!isSubscription && !isForcedAutoPay && <FrictionlessLogin />}
          <style jsx>{`
            :global(.pay-with-title) {
              font-family: AvenirNextforINTUIT-Demi;
              margin-bottom: 13px;
              display: block;
              font-size: ${fontSize.sm};
              cursor: text;
            }

            .new-payment-method-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 12px;
              font-family: AvenirNextforINTUIT-Demi;
              color: ${colors.gray02};
              font-weight: 600;

              @media screen and (max-width: ${breakpoints.md}) {
                flex-direction: column;
                align-items: flex-start;
              }

              .back-btn {
                @media screen and (max-width: ${breakpoints.md}) {
                  order: -1;
                }
              }
            }

            .payment-tabs-desktop {
              @media screen and (max-width: ${breakpoints.md}) {
                display: none;
              }
            }

            .payment-tabs-mobile {
              display: none;
              @media screen and (max-width: ${breakpoints.md}) {
                display: block;
              }
            }

            .truste-w {
              @media screen and (max-width: ${breakpoints.md}) {
                display: block;
              }
              display: none;

              margin-bottom: 15px;
            }

            .top-pm-bugs {
              @media screen and (min-width: ${breakpoints.lg}) {
                display: none;
              }
            }

            .payment-method-label {
              font-family: AvenirNextforINTUIT-Demi;
              font-size: ${fontSize.xxs};
              color: ${colors.whiteGray};
              text-transform: uppercase;
              display: block;
              margin-bottom: 4px;
              @media screen and (max-width: ${breakpoints.md}) {
                margin-bottom: 0px;
              }
            }
          `}</style>
        </FormDraftProvider.Provider>
      </Fragment>
    );
  }
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    ...bindActionCreators(
      {
        paymentMethodTypeChange,
        changeSelectedWallet,
        bindFormSubmission: walletActions.bindFormSubmission,
        createPayment: walletTokenizationPaymentCreation,
        stopPayment: paymentActions.cancelPayment,
        showModal: modalActions.show,
        hideModal: modalActions.hide,
        showToast: toastActions.show,
        onDeletePaymentMethodClicked: walletActions.onDeletePaymentMethodClicked,
        onUpdatePaymentMethodClicked: walletActions.onUpdatePaymentMethodClicked,
        deleteWallet,
        cancelDeleteWallet: walletActions.deleteWalletCancel,
        onCvvInputValueChange: walletActions.onCvvInputValueChange,
        setIsNewWalletUI: walletActions.setIsNewWalletUI,
        updateCountry: paymentActions.updateCountry,
        updateFrictionlessLoginCountry: authActions.updateFrictionlessCheckoutCountry,
        setSaveToMerchantWalletCheckboxChecked:
          walletActions.setSaveToMerchantWalletCheckboxChecked,
      },
      dispatch
    ),
  };
}

function mapStateToProps(store) {
  const { companyInfo, sale, config, payment, insight, wallet, auth, ixp, featureFlags } = store;

  // For batch payments feature, compute allowed payment methods dynamically
  // based on selected invoices
  let paymentMethodsOrder;
  const batchPaymentsEnabled = isBatchPaymentsEnabled(store);
  const selectedBatchInvoices = insightSelectors.selectedBatchInvoices(insight);

  // Check if only the default invoice is selected (for showing autopay)
  const onlyDefaultInvoiceSelected =
    batchPaymentsEnabled &&
    selectedBatchInvoices?.length === 1 &&
    selectedBatchInvoices[0]?.defaultInvoice;

  if (batchPaymentsEnabled && selectedBatchInvoices && selectedBatchInvoices.length > 0) {
    // Get allowed payment methods based on selected batch invoices
    const region = companyInfoSelectors.regionSelector(companyInfo);
    const batchAllowedMethods = getAllowedBatchPaymentMethods(
      selectedBatchInvoices,
      wallet.enabledPaymentMethods || [],
      region
    );
    // Order them according to the default payment order
    paymentMethodsOrder = getPaymentMethodsOrder({
      enabledPaymentMethods: batchAllowedMethods,
    });
  } else if (
    batchPaymentsEnabled &&
    (!selectedBatchInvoices || selectedBatchInvoices.length === 0)
  ) {
    // When batch is enabled but no invoices selected, show only basic payment methods (bank, dc, cc)
    // Filter out PayPal, Venmo, Apple Pay
    const basicPaymentMethods = (wallet.enabledPaymentMethods || []).filter(
      (method) => !['paypal', 'paypal_ppaam', 'venmo', 'pp', 'ap'].includes(method)
    );
    paymentMethodsOrder = getPaymentMethodsOrder({
      enabledPaymentMethods: basicPaymentMethods,
    });
  } else {
    // Use regular payment methods order
    paymentMethodsOrder = getPaymentMethodsOrder({
      enabledPaymentMethods: wallet.enabledPaymentMethods,
    });
  }

  const isSurchargingEnabled = isSurchargingEnabledSelector(store);
  paymentMethodsOrder = filterSurchargeEligibleMethods(paymentMethodsOrder, isSurchargingEnabled);

  return {
    companyLocale: companyInfoSelectors.localeSelector(companyInfo),
    companyName: companyInfoSelectors.nameSelector(companyInfo),
    region: companyInfoSelectors.regionSelector(companyInfo),
    auth,
    config,
    payment,
    sale,
    wallet,
    featureFlags,
    insight,
    ixp,
    isInvoiceGratuityEnabled: displayGratuityFeature(store),
    paymentMethodsOrder,
    isSubscription: saleSelectors.isSubscription(sale),
    isOneToMany: saleSelectors.isOneToManyPaymentRequest(sale),
    shouldShowSignInCTA: shouldShowSignInCTASelector(store),
    isSignInCTADecisionPending: isSignInCTADecisionPendingSelector(store),
    isIdentitySignInWidgetCompleted: isIdentitySignInWidgetCompletedSelector(store),
    isFrictionlessPhase3Enabled: isFrictionlessLoginPhase3Enabled(store),
    isForcedAutoPay: isForcedAutopayRecurringSelector(store),
    onlyDefaultInvoiceSelected,
    isBatchPaymentsEnabled: batchPaymentsEnabled,
    isBatchCOFEnabled: isBatchCOFEnabled(store),
    isInNameValidationExperiment: isInNameValidationExperiment(store),
    isInEnhancementsHoldoutExperiment: isInEnhancementsHoldoutExperiment(store),
    domainId: saleSelectors.domainIdSelector(sale, auth)?.replace('INVOICE:', ''),
    isSurchargingEnabled,
  };
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(Payment));
